import { useEffect, useRef, useState } from 'react'
import {
  Image as ImageIcon,
  Video,
  Music,
  Type as TypeIcon,
  Captions,
  Trash2,
  UploadCloud,
  FileQuestion,
  Download,
  Play,
  Pause,
  Expand,
} from 'lucide-react'
import { appApiBaseUrl, appFetch, AppApiError, uploadFile } from '@/shared/api/app-client'
import type {
  ApiResponse,
  Asset,
  AssetUploadResult,
  LibraryCategory,
  PaginatedResponse,
} from '@/shared/api/app-types'
import { cn } from '@/shared/ui/cn'
import { AssetPreviewDialog } from './asset-preview-dialog'

/** tab 配置：标签 → library_category 参数 */
type AssetCategory = LibraryCategory | 'all'

const TABS: { label: string; category: AssetCategory; icon: typeof ImageIcon }[] = [
  { label: '全部', category: 'all', icon: FileQuestion },
  { label: '图片', category: 'image', icon: ImageIcon },
  { label: '视频', category: 'video', icon: Video },
  { label: '音频', category: 'audio', icon: Music },
  { label: '字体', category: 'font', icon: TypeIcon },
  { label: '字幕', category: 'subtitle', icon: Captions },
]

const PAGE_SIZE = 50

/** 将 /uploads/... 相对路径补全为后端完整 URL */
function resolveUrl(url: string | null): string | null {
  if (!url) return null
  if (url.startsWith('/uploads/')) {
    return `${appApiBaseUrl}${url}`
  }
  return url
}

/** 格式化文件大小 */
function formatFileSize(bytes: number | null): string {
  if (!bytes) return '-'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

/** 格式化时长（ms → mm:ss） */
function formatDuration(ms: number | null): string {
  if (!ms) return ''
  const totalSec = Math.floor(ms / 1000)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

export function AssetsCanvas({ initialCategory = 'all' }: { initialCategory?: AssetCategory }) {
  const [activeCategory, setActiveCategory] = useState<AssetCategory>(initialCategory)
  const [assets, setAssets] = useState<Asset[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [refreshKey, setRefreshKey] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [previewIndex, setPreviewIndex] = useState(-1)
  const [storage, setStorage] = useState<{ maxUploadBytes: number; assetStorageBytes: number; assetUsedBytes: number; assetRemainingBytes: number } | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const refresh = () => {
    setPage(1)
    setRefreshKey((k) => k + 1)
  }

  // 当前 tab 配置（在 TABS 中查找，必然存在）
  const activeTab: { label: string; category: AssetCategory; icon: typeof ImageIcon } =
    TABS.find((t) => t.category === activeCategory) ?? {
      label: '全部',
      category: 'all',
      icon: FileQuestion,
    }

  // 切换 tab 时重置分页
  useEffect(() => {
    setPage(1)
    setRefreshKey((k) => k + 1)
  }, [activeCategory])

  // 加载列表（首页）
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    const params = new URLSearchParams({
      page: '1',
      pageSize: String(PAGE_SIZE),
    })
    if (activeCategory !== 'all') params.set('libraryCategory', activeCategory)

    void appFetch<ApiResponse<PaginatedResponse<Asset>>>(`/api/assets?${params.toString()}`)
      .then((res) => {
        if (!cancelled) {
          setAssets(res.data.items)
          setTotal(res.data.total)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : '加载素材失败')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [activeCategory, refreshKey])

  useEffect(() => {
    void appFetch<ApiResponse<{ maxUploadBytes: number; assetStorageBytes: number; assetUsedBytes: number; assetRemainingBytes: number }>>('/api/storage/summary').then((result) => setStorage(result.data)).catch(() => setStorage(null))
  }, [refreshKey])

  // 加载更多
  async function loadMore() {
    const nextPage = page + 1
    const params = new URLSearchParams({
      page: String(nextPage),
      pageSize: String(PAGE_SIZE),
    })
    if (activeCategory !== 'all') params.set('libraryCategory', activeCategory)
    try {
      const res = await appFetch<ApiResponse<PaginatedResponse<Asset>>>(
        `/api/assets?${params.toString()}`,
      )
      setAssets((prev) => [...prev, ...res.data.items])
      setPage(nextPage)
    } catch (err) {
      alert(err instanceof AppApiError ? err.message : '加载更多失败')
    }
  }

  // 上传
  async function handleUpload(file: File) {
    if (storage && file.size > storage.maxUploadBytes) {
      alert(`单文件不能超过 ${formatFileSize(storage.maxUploadBytes)}。`)
      return
    }
    if (storage && file.size > storage.assetRemainingBytes) {
      alert('云端素材空间不足，请删除不再需要的素材或升级套餐。')
      return
    }
    setUploading(true)
    setUploadProgress(0)
    try {
      await uploadFile<ApiResponse<AssetUploadResult>>(
        '/api/assets/upload',
        file,
        (percent) => setUploadProgress(percent),
      )
      refresh()
    } catch (err) {
      alert(err instanceof AppApiError ? err.message : '上传失败')
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) {
      void handleUpload(file)
    }
    // 清空 input，允许重复选择同一文件
    e.target.value = ''
  }

  function requestUpload() {
    fileInputRef.current?.click()
  }

  // 删除
  async function handleDelete(asset: Asset) {
    if (!window.confirm(`确定删除「${asset.title || asset.fileName}」吗？此操作不可撤销。`)) return
    setDeletingId(asset.id)
    try {
      await appFetch<ApiResponse<unknown>>(`/api/assets/${asset.id}`, { method: 'DELETE' })
      setAssets((prev) => prev.filter((a) => a.id !== asset.id))
      setTotal((t) => Math.max(0, t - 1))
    } catch (err) {
      alert(err instanceof AppApiError ? err.message : '删除失败')
    } finally {
      setDeletingId(null)
    }
  }

  const hasMore = assets.length < total

  return (
    <div className="px-6 py-6">
      <h1 className="mb-4 text-2xl font-semibold text-foreground">素材库</h1>
      {storage ? <div className="mb-4 flex flex-wrap gap-x-5 gap-y-1 border border-border bg-card px-4 py-3 text-xs text-muted-foreground"><span>云端素材空间：{formatFileSize(storage.assetUsedBytes)} / {formatFileSize(storage.assetStorageBytes)}</span><span>剩余：{formatFileSize(storage.assetRemainingBytes)}</span><span>单文件最大：{formatFileSize(storage.maxUploadBytes)}</span><span>本地项目不占云端空间</span></div> : null}

      {/* tab switcher */}
      <div className="flex items-center gap-4 border-b border-border py-4">
        <div className="inline-flex items-center gap-1 rounded-full bg-card p-1">
          {TABS.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.category}
                onClick={() => setActiveCategory(tab.category)}
                className={cn(
                  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors',
                  tab.category === activeCategory
                    ? 'bg-accent text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="size-3.5" />
                {tab.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* section header */}
      <div className="flex items-center justify-between py-6">
        <div>
          <div className="text-base font-semibold text-foreground">{activeTab.label}</div>
          <div className="mt-1.5 text-sm text-muted-foreground">
            上传并整理你想用于项目的媒体素材。共 {total} 个
          </div>
        </div>
        <button
          onClick={requestUpload}
          disabled={uploading}
          className="inline-flex h-9 items-center justify-center gap-2 rounded-full bg-primary px-4 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <UploadCloud className="size-4" />
          {uploading ? `上传中 ${uploadProgress}%` : '上传素材'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={onFileChange}
          accept={activeCategory === 'all' ? undefined : getAcceptAttr(activeCategory)}
        />
      </div>

      {/* upload progress */}
      {uploading && (
        <div className="mb-4 h-1 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${uploadProgress}%` }}
          />
        </div>
      )}

      {/* content */}
      {error ? (
        <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
          {error}
        </div>
      ) : loading ? (
        <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
          加载中...
        </div>
      ) : assets.length === 0 ? (
        <EmptyState
          icon={activeTab.icon}
          category={activeCategory}
        />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {assets.map((asset, i) => (
              <AssetCard
                key={asset.id}
                asset={asset}
                category={asset.libraryCategory as LibraryCategory}
                onDelete={handleDelete}
                deleting={deletingId === asset.id}
                onPreview={() => setPreviewIndex(i)}
              />
            ))}
          </div>
          {hasMore && (
            <div className="mt-6 flex justify-center">
              <button
                onClick={loadMore}
                className="inline-flex h-9 items-center justify-center rounded-full border border-border bg-card px-6 text-xs font-medium text-foreground transition-colors hover:bg-accent"
              >
                加载更多
              </button>
            </div>
          )}
        </>
      )}

      {/* 素材预览弹窗 */}
      <AssetPreviewDialog
        assets={assets}
        index={previewIndex}
        category={previewIndex >= 0 ? (assets[previewIndex]?.libraryCategory as LibraryCategory | undefined) ?? 'image' : 'image'}
        onIndexChange={setPreviewIndex}
        onOpenChange={(open) => {
          if (!open) setPreviewIndex(-1)
        }}
      />
    </div>
  )
}

/** 触发下载（通过创建 a 标签，带 download 属性） */
function triggerDownload(url: string, fileName: string) {
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.target = '_blank'
  a.rel = 'noopener noreferrer'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

/** 根据 category 返回 input accept 属性 */
function getAcceptAttr(category: LibraryCategory): string {
  switch (category) {
    case 'image':
      return 'image/jpeg,image/png,image/webp,image/gif'
    case 'video':
      return 'video/mp4,video/quicktime,video/webm,video/x-matroska,video/x-msvideo'
    case 'audio':
      return 'audio/mpeg,audio/wav,audio/ogg'
    case 'font':
      return 'font/ttf,font/otf,font/woff,font/woff2'
    case 'subtitle':
      return '.srt,.vtt,.ass,application/x-subrip,text/vtt,text/x-ass,application/x-ass'
  }
}

/** 单个素材卡片 */
function AssetCard({
  asset,
  category,
  onDelete,
  deleting,
  onPreview,
}: {
  asset: Asset
  category: LibraryCategory
  onDelete: (asset: Asset) => void
  deleting: boolean
  onPreview: () => void
}) {
  const publicUrl = resolveUrl(asset.publicUrl)
  const thumbnailUrl = resolveUrl(asset.thumbnailUrl)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)

  function toggleInlineVideoPlayback() {
    const video = videoRef.current
    if (!video) return

    if (video.paused) {
      void video.play().then(() => {
        setIsVideoPlaying(true)
      }).catch(() => {
        setIsVideoPlaying(false)
      })
      return
    }

    video.pause()
    setIsVideoPlaying(false)
  }

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-lg border border-border bg-card transition-colors hover:border-primary/40">
      {/* preview area */}
      <div
        className="relative flex aspect-square w-full items-center justify-center overflow-hidden bg-background"
      >
        {category === 'image' && publicUrl ? (
          <button
            type="button"
            onClick={onPreview}
            className="size-full"
            title="点击预览"
          >
            <img
              src={publicUrl}
              alt={asset.fileName}
              className="size-full object-cover transition-transform group-hover:scale-105"
              loading="lazy"
            />
          </button>
        ) : category === 'video' ? (
          publicUrl ? (
            <>
              <button
                type="button"
                onClick={toggleInlineVideoPlayback}
                className="relative size-full"
                title={isVideoPlaying ? '点击暂停' : '点击播放'}
              >
                <video
                  ref={videoRef}
                  src={publicUrl}
                  poster={thumbnailUrl ?? undefined}
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="size-full object-cover transition-transform group-hover:scale-105"
                  onLoadedMetadata={(event) => {
                    const video = event.currentTarget
                    if (video.duration > 0 && video.currentTime === 0) {
                      video.currentTime = 0.01
                    }
                  }}
                  onPause={() => setIsVideoPlaying(false)}
                  onPlay={() => setIsVideoPlaying(true)}
                  onEnded={() => setIsVideoPlaying(false)}
                />
                <span className="pointer-events-none absolute inset-0 bg-black/10 transition-colors group-hover:bg-black/5" />
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <span className="flex size-11 items-center justify-center rounded-full bg-black/55 text-white shadow-lg backdrop-blur-sm transition-transform group-hover:scale-105">
                    {isVideoPlaying ? <Pause className="size-4" /> : <Play className="ml-0.5 size-4" />}
                  </span>
                </span>
              </button>
              <button
                type="button"
                onClick={onPreview}
                className="absolute left-1.5 top-1.5 z-10 inline-flex h-7 items-center gap-1 rounded-full bg-black/60 px-2.5 text-[10px] font-medium text-white transition-colors hover:bg-black/80"
                title="打开大图预览"
              >
                <Expand className="size-3" />
                预览
              </button>
            </>
          ) : (
            <Video className="size-8 text-muted-foreground" />
          )
        ) : category === 'audio' ? (
          <AudioPreviewArea publicUrl={publicUrl} />
        ) : category === 'font' ? (
          <FontCardPreview asset={asset} />
        ) : category === 'subtitle' ? (
          <Captions className="size-8 text-muted-foreground" />
        ) : (
          <FileQuestion className="size-8 text-muted-foreground" />
        )}

        {/* duration badge for video/audio */}
        {(category === 'video' || category === 'audio') && asset.durationMs ? (
          <span className="absolute bottom-1.5 right-1.5 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
            {formatDuration(asset.durationMs)}
          </span>
        ) : null}

        {/* hover 遮罩 + 操作按钮 */}
        <span className="pointer-events-none absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/20" />
        <span className="absolute right-1.5 top-1.5 z-10 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          {publicUrl && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation()
                triggerDownload(publicUrl, asset.fileName)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation()
                  e.preventDefault()
                  triggerDownload(publicUrl, asset.fileName)
                }
              }}
              className="flex size-7 cursor-pointer items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-primary"
              title="下载"
            >
              <Download className="size-3.5" />
            </span>
          )}
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation()
              onDelete(asset)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation()
                e.preventDefault()
                onDelete(asset)
              }
            }}
            className={cn(
              'flex size-7 cursor-pointer items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-red-500',
              deleting && 'opacity-0',
            )}
            title="删除"
          >
            <Trash2 className="size-3.5" />
          </span>
        </span>
      </div>

      {/* info */}
      <div className="flex flex-col gap-0.5 px-2.5 py-2">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-xs font-medium text-foreground" title={asset.fileName}>
            {asset.title || asset.fileName}
          </span>
          {(category === 'video' || category === 'audio' || category === 'image') ? (
            <button
              type="button"
              onClick={onPreview}
              className="shrink-0 text-[10px] text-muted-foreground transition-colors hover:text-foreground"
            >
              预览
            </button>
          ) : null}
        </div>
        <div className="flex items-center justify-between text-[10px] text-muted-foreground">
          <span>{formatFileSize(asset.fileSize)}</span>
          {asset.imageWidth && asset.imageHeight ? (
            <span>
              {asset.imageWidth}×{asset.imageHeight}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  )
}

/** 音频卡片内联播放区：中央播放/暂停按钮 + 底部进度条 */
function AudioPreviewArea({
  publicUrl,
}: {
  publicUrl: string | null
}) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)

  function toggle() {
    const audio = audioRef.current
    if (!audio) return
    if (audio.paused) {
      void audio.play().catch(() => setIsPlaying(false))
    } else {
      audio.pause()
    }
  }

  function handleSeek(e: React.MouseEvent<HTMLSpanElement>) {
    const audio = audioRef.current
    if (!audio || !audio.duration) return
    const rect = e.currentTarget.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    audio.currentTime = ratio * audio.duration
  }

  return (
    <>
      {publicUrl ? (
        <button
          type="button"
          onClick={toggle}
          className="relative size-full"
          title={isPlaying ? '点击暂停' : '点击播放'}
        >
          <span className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/5 to-primary/10">
            <span className="flex size-11 items-center justify-center rounded-full bg-black/55 text-white shadow-lg backdrop-blur-sm transition-transform group-hover:scale-105">
              {isPlaying ? <Pause className="size-4" /> : <Play className="ml-0.5 size-4" />}
            </span>
          </span>
          {/* 底部进度条 */}
          <span
            className="absolute inset-x-0 bottom-0 h-1 cursor-pointer bg-black/10"
            onClick={(e) => {
              e.stopPropagation()
              handleSeek(e)
            }}
          >
            <span
              className="block h-full bg-primary"
              style={{ width: `${progress}%` }}
            />
          </span>
          {/* 当前时间提示 */}
          {isPlaying || currentTime > 0 ? (
            <span className="absolute left-1.5 top-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
              {formatDuration(currentTime * 1000)}
            </span>
          ) : null}
        </button>
      ) : (
        <Music className="size-8 text-muted-foreground" />
      )}
      <audio
        ref={audioRef}
        src={publicUrl ?? undefined}
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => {
          setIsPlaying(false)
          setProgress(0)
          setCurrentTime(0)
        }}
        onTimeUpdate={(e) => {
          const audio = e.currentTarget
          if (audio.duration) {
            setProgress((audio.currentTime / audio.duration) * 100)
            setCurrentTime(audio.currentTime)
          }
        }}
      />
    </>
  )
}

/** 字体卡片预览：动态加载字体文件并实时渲染样本文字 */
function FontCardPreview({ asset }: { asset: Asset }) {
  const publicUrl = resolveUrl(asset.publicUrl)
  // 用 asset.id 作为 family 名后缀，避免与已注册字体冲突
  const family = `fc-${asset.id}`
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (!publicUrl) return
    let cancelled = false
    const fontFace = new FontFace(family, `url("${publicUrl}")`)
    fontFace
      .load()
      .then(() => {
        if (!cancelled) {
          document.fonts.add(fontFace)
          setLoaded(true)
        }
      })
      .catch(() => {
        // 加载失败，静默 fallback 到默认字体
      })

    return () => {
      cancelled = true
      document.fonts.delete(fontFace)
    }
  }, [family, publicUrl])

  return (
    <div
      className="flex size-full flex-col items-center justify-center gap-1 bg-gradient-to-br from-background to-muted/40 px-2"
      style={loaded ? { fontFamily: `'${family}', sans-serif` } : undefined}
    >
      <span className="text-2xl font-medium leading-none text-foreground">永 Aa</span>
      <span className="text-[11px] leading-tight text-muted-foreground">中文 ABC 123</span>
    </div>
  )
}

/** 空状态 */
function EmptyState({
  icon: Icon,
  category,
}: {
  icon: typeof ImageIcon
  category: AssetCategory
}) {
  const labels: Record<AssetCategory, string> = {
    all: '素材',
    image: '图片',
    video: '视频',
    audio: '音频',
    font: '字体',
    subtitle: '字幕',
  }
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border py-16">
      <div className="flex size-12 items-center justify-center rounded-full bg-card text-muted-foreground">
        <Icon className="size-6" />
      </div>
      <div className="text-center">
        <div className="text-sm font-medium text-foreground">还没有{labels[category]}</div>
        <div className="mt-1 text-xs text-muted-foreground">上传你的第一个{labels[category]}</div>
      </div>
    </div>
  )
}
